/**
 * Freeze / unfreeze the page behind a full-screen overlay (the mobile nav
 * drawer, the mobile date sheet) WITHOUT touching the scroll position or
 * layout.
 *
 * Earlier attempts both failed:
 *  · `overflow: hidden` on <html> clamps scrollTop to 0 → the page behind
 *    snapped to the top the moment a sheet opened.
 *  · pinning <body> with `position: fixed; top: -Y` preserved the offset
 *    but killed every `position: sticky` descendant — including the app
 *    header, which vanished and left a blank band above the sheet on
 *    pages that were scrolled.
 *
 * This version leaves the document completely alone and simply swallows
 * the scroll gestures: non-passive touchmove/wheel listeners preventDefault
 * unless the gesture happens inside the overlay's own scroller
 * (`allowWithin`). Nothing moves, nothing reflows, sticky stays sticky.
 *
 * That last point matters more here than in console-v2: `styles.css` puts
 * `overflow-x: clip` on <html> for the redesigned scope, which BLOCKS the
 * usual body→viewport overflow propagation. Any overflow written to <body>
 * would turn body itself into a scroll container and unstick every sticky
 * layer on the page (the app header, the mail rail, `thead th { top: 58px }`)
 * — the same failure `body.dashboard-active.modal-open` exists to undo.
 *
 * `allowWithin` is a CSS selector for the overlay's own scroller. Every caller
 * must `unlockPageScroll()` on close AND in `ngOnDestroy`: a route change can
 * tear the component down while the overlay is open, which would otherwise
 * leave the whole app unscrollable.
 *
 * OWNERSHIP. The lock is a single global, but the callers are not: both close
 * on an "anywhere on the document" click, and those handlers fire whether or
 * not that component's own overlay is open. So a tap inside the open nav
 * drawer also runs every mounted date filter's close handler, and an
 * unconditional unlock there released the DRAWER's lock while the drawer was
 * still on screen. `key` — pass the component instance — makes an unlock a
 * no-op for anyone who does not hold the lock.
 */
let handler: ((event: Event) => void) | null = null;
let owner: unknown = null;

export function lockPageScroll(allowWithin: string, key: unknown): void {
  if (handler) {
    return; // already locked, by this caller or another
  }
  owner = key;
  handler = (event: Event) => {
    const target = event.target as Element | null;
    if (target?.closest?.(allowWithin)) {
      return; // let the overlay's own content scroll
    }
    event.preventDefault();
  };
  document.addEventListener('touchmove', handler, {passive: false});
  document.addEventListener('wheel', handler, {passive: false});
  document.documentElement.classList.add('nav-locked');
}

export function unlockPageScroll(key: unknown): void {
  if (!handler || owner !== key) {
    return;
  }
  document.removeEventListener('touchmove', handler);
  document.removeEventListener('wheel', handler);
  handler = null;
  owner = null;
  document.documentElement.classList.remove('nav-locked');
}
